/**
 * Generic auth error landing. Used when /api/auth/callback or
 * /api/auth/idp-callback fails for any reason (Cognito returned
 * error, missing/replayed code, state mismatch, token verification
 * failure, role check failure). The API redirects here instead of
 * rendering raw JSON so the user lands on a real page they can
 * recover from.
 *
 * `?from=idp` is appended by the IdP-init callback. We suppress the
 * "Try signing in again" button in that case — that button starts
 * the SP-initiated flow, which is the wrong recovery path for
 * someone who came in through their IdP's tile (Okta for external
 * advisors, EntraID for internal staff). They need to return to the
 * tile they came from.
 */

import { Button, Card, Container } from 'react-bootstrap';
import { useReplaceCallbackHistory } from './use-replace-callback-history.js';

export function AuthErrorPage() {
    useReplaceCallbackHistory('/auth/error');
    const fromIdp =
        new URLSearchParams(window.location.search).get('from') === 'idp';

    return (
        <div className="d-flex align-items-center min-vh-100 bg-light py-4">
            <Container style={{ maxWidth: 520 }}>
                <Card className="shadow-sm border-0">
                    <Card.Body className="p-5 text-center">
                        <h2 className="mb-3">Sign-in didn&rsquo;t complete</h2>
                        {fromIdp ? (
                            <p className="text-secondary mb-0">
                                Please return to the tile you signed in from
                                and try again. If the issue persists, contact
                                your administrator.
                            </p>
                        ) : (
                            <>
                                <p className="text-secondary mb-4">
                                    We couldn&rsquo;t finish signing you in. This
                                    usually clears up if you start over from the
                                    home page.
                                </p>
                                <Button
                                    variant="primary"
                                    href="/api/auth/login"
                                    className="px-4"
                                >
                                    Try signing in again
                                </Button>
                            </>
                        )}
                    </Card.Body>
                </Card>
            </Container>
        </div>
    );
}
